import React, { useState, useContext } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useNavigate } from "react-router-dom";
import { AdminDataContext } from "../../../useContext/AdminContext";
import API, { setAuthToken } from "../../../api";

const ChangePassword = () => {
  const { snackbar, setIsAuthenticated } = useContext(AdminDataContext);
  const navigate = useNavigate();

  const [values, setValues] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
    showOldPassword: false,
    showNewPassword: false,
    showConfirmPassword: false,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const handleChange = (prop) => (event) => {
    setValues({ ...values, [prop]: event.target.value });
    // Clear error when user starts typing
    if (errors[prop]) {
      setErrors({ ...errors, [prop]: "" });
    }
  };

  const handleClickShowPassword = (field) => {
    setValues({
      ...values,
      [field]: !values[field],
    });
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = {
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    };

    // Old password validation
    if (!values.oldPassword) {
      newErrors.oldPassword = "Current password is required";
      isValid = false;
    }

    // New password validation
    if (!values.newPassword) {
      newErrors.newPassword = "New password is required";
      isValid = false;
    } else if (values.newPassword.length < 6) {
      newErrors.newPassword = "Password must be at least 6 characters";
      isValid = false;
    } else if (values.newPassword === values.oldPassword) {
      newErrors.newPassword =
        "New password must be different from current password";
      isValid = false;
    }

    // Confirm password validation
    if (!values.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your new password";
      isValid = false;
    } else if (values.confirmPassword !== values.newPassword) {
      newErrors.confirmPassword = "Passwords do not match";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleChangePassword = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await API.post("/api/v1/users/change-password", {
        oldPassword: values.oldPassword,
        newPassword: values.newPassword,
      });

      if (response.data.success) {
        snackbar("Password changed successfully! Logging out...", "success");

        // Wait for snackbar to show
        setTimeout(async () => {
          // Logout the user
          try {
            await API.post("/api/v1/users/logout");
          } catch (logoutError) {
            console.error("Logout error:", logoutError);
          }

          // Clear authentication tokens
          setAuthToken(null);
          localStorage.removeItem("accessToken");

          // Update authentication state
          setIsAuthenticated(false);

          // Redirect to login page
          navigate("/login");
        }, 1500); // Give user time to see the success message
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        "Failed to change password. Please try again.";
      snackbar(errorMessage, "error");
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setValues({
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
      showOldPassword: false,
      showNewPassword: false,
      showConfirmPassword: false,
    });
    setErrors({
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleChangePassword();
    }
  };

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        maxWidth: 500,
        mx: "auto",
      }}
    >
      {/* Header */}
      <Typography
        variant="h4"
        sx={{
          mb: 4,
          fontSize: { xs: "1.5rem", md: "1.75rem" },
          color: "#333",
          fontWeight: 600,
          textAlign: "center",
        }}
      >
        Change Password
      </Typography>

      {/* Change Password Form */}
      <Paper
        sx={{
          p: { xs: 3, md: 4 },
          backgroundColor: "#ffffff",
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
          borderRadius: 2,
        }}
      >
        {/* Old Password Field */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              fontWeight: 500,
              color: "#333",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            Old Password
            <Box
              component="span"
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: "#4CAF50",
                display: "inline-block",
              }}
            />
          </Typography>
          <TextField
            size="small"
            fullWidth
            placeholder="Enter your old password"
            type={values.showOldPassword ? "text" : "password"}
            value={values.oldPassword}
            onChange={handleChange("oldPassword")}
            onKeyPress={handleKeyPress}
            error={!!errors.oldPassword}
            helperText={errors.oldPassword}
            disabled={isLoading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => handleClickShowPassword("showOldPassword")}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                    sx={{
                      color: "#999",
                      "&:hover": {
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    {values.showOldPassword ? (
                      <VisibilityOff />
                    ) : (
                      <Visibility />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#F5F5F5",
                borderRadius: "8px",
                "& fieldset": {
                  borderColor: "transparent",
                },
                "&:hover fieldset": {
                  borderColor: "#42A5F5",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#42A5F5",
                  borderWidth: 2,
                },
                "&.Mui-error fieldset": {
                  borderColor: "#d32f2f",
                },
              },
            }}
          />
        </Box>

        {/* New Password Field */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              fontWeight: 500,
              color: "#333",
            }}
          >
            New Password
          </Typography>
          <TextField
            size="small"
            fullWidth
            placeholder="Enter your new password"
            type={values.showNewPassword ? "text" : "password"}
            value={values.newPassword}
            onChange={handleChange("newPassword")}
            onKeyPress={handleKeyPress}
            error={!!errors.newPassword}
            helperText={errors.newPassword}
            disabled={isLoading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => handleClickShowPassword("showNewPassword")}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                    sx={{
                      color: "#999",
                      "&:hover": {
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    {values.showNewPassword ? (
                      <VisibilityOff />
                    ) : (
                      <Visibility />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#F5F5F5",
                borderRadius: "8px",
                "& fieldset": {
                  borderColor: "transparent",
                },
                "&:hover fieldset": {
                  borderColor: "#42A5F5",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#42A5F5",
                  borderWidth: 2,
                },
                "&.Mui-error fieldset": {
                  borderColor: "#d32f2f",
                },
              },
            }}
          />
          {/* Password Requirements */}
          {values.newPassword && (
            <Box sx={{ mt: 1.5 }}>
              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                {/* <Typography
                  variant="caption"
                  component="li"
                  sx={{
                    color: values.newPassword.length >= 6 ? "#4CAF50" : "#999",
                  }}
                >
                  Minimum characters is 6
                </Typography> */}
              </Box>
            </Box>
          )}
        </Box>

        {/* Confirm Password Field */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              fontWeight: 500,
              color: "#333",
            }}
          >
            Confirm New Password
          </Typography>
          <TextField
            size="small"
            fullWidth
            placeholder="Enter your confirm new password"
            type={values.showConfirmPassword ? "text" : "password"}
            value={values.confirmPassword}
            onChange={handleChange("confirmPassword")}
            onKeyPress={handleKeyPress}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword}
            disabled={isLoading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() =>
                      handleClickShowPassword("showConfirmPassword")
                    }
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                    sx={{
                      color: "#999",
                      "&:hover": {
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    {values.showConfirmPassword ? (
                      <VisibilityOff />
                    ) : (
                      <Visibility />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#F5F5F5",
                borderRadius: "8px",
                "& fieldset": {
                  borderColor: "transparent",
                },
                "&:hover fieldset": {
                  borderColor: "#42A5F5",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#42A5F5",
                  borderWidth: 2,
                },
                "&.Mui-error fieldset": {
                  borderColor: "#d32f2f",
                },
              },
            }}
          />
        </Box>

        {/* Change Password Button */}
        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={handleChangePassword}
          disabled={isLoading}
          sx={{
            borderRadius: "30px",
            padding: "12px 24px",
            fontSize: "1rem",
            fontWeight: 600,
            textTransform: "none",
            background: "linear-gradient(135deg, #42A5F5 0%, #1565C0 100%)",
            boxShadow: "0 4px 15px rgba(66, 165, 245, 0.4)",
            transition: "all 0.3s ease",
            "&:hover": {
              background: "linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)",
              boxShadow: "0 6px 20px rgba(66, 165, 245, 0.6)",
              transform: "translateY(-2px)",
            },
            "&:active": {
              transform: "translateY(0)",
            },
            "&:disabled": {
              background: "#ccc",
              color: "#666",
            },
          }}
        >
          {isLoading ? (
            <CircularProgress size={24} sx={{ color: "#fff" }} />
          ) : (
            "Change Password"
          )}
        </Button>

        {/* Forgot Password Link */}
        {/* <Box sx={{ textAlign: "center", mt: 2 }}>
          <Typography
            variant="body2"
            component="span"
            sx={{
              color: "#42A5F5",
              cursor: "pointer",
              textDecoration: "none",
              fontWeight: 500,
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            Forgot Password?
          </Typography>
        </Box> */}
      </Paper>
    </Box>
  );
};

export default ChangePassword;
